import { CSSProperties } from 'react'
import { formatCurrency } from '@amusnet/amusnet-common'
import { gin, NotificationType, showReactUINotification } from '@amusnet/react-ui'
import { translate } from '@amusnet/translations'
import { Translate } from '@amusnet/translations/react'
import { CountdownCounterDisplayItems } from '@components/CountdownFreeSpinsBonus/types'
import {
    useConfigurationStore,
    useBuyBonusStore,
    useFreeBuyBonusStore,
    useCountdownFSBonusStore,
    useFreeSpinsBonusesStore,
} from '@stores'
import { getCountdownDisplayItems, getCountdownValuesFromBonus, modifyFreeBuyBonuses } from '@utils/bonus'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import {
    Bonus,
    BonusTypes,
    GameUIMessageType,
    BonusesPlayNowMessage,
    BuyBonusFeature,
    BonusSystemState,
} from 'game-ui-types'

const FEATURE_IMAGE_PREFIX = 'buy-bonus-button-image-card-0'
const FALLBACK_IMAGE_NAME = 'buy-bonus-button-image-card-01.png'

const useBonusSystem = () => {
    // Configuration Store
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)

    // Buy Bonus Global Settings Store
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)

    // Free Buy Bonus Store
    const setHasFBBActivatedBonus = useFreeBuyBonusStore((state) => state.setHasActivatedBonus)
    const setFBBActiveBonus = useFreeBuyBonusStore((state) => state.setActiveBonus)
    const setFBBonusSystemState = useFreeBuyBonusStore((state) => state.setBonusSystemState)
    const setFBBonuses = useFreeBuyBonusStore((state) => state.setBonuses)

    // Free Spins Store
    const setFSActiveBonus = useFreeSpinsBonusesStore((state) => state.setActiveBonus)
    const setFSBonusSystemState = useFreeSpinsBonusesStore((state) => state.setBonusSystemState)

    // Countdown Free Spins Store
    const setCFSActiveBonus = useCountdownFSBonusStore((state) => state.setActiveBonus)
    const setCFSBonusSystemState = useCountdownFSBonusStore((state) => state.setBonusSystemState)

    const showBonusAwardedNotification = (bonuses: Bonus[]) => {
        if (!bonuses || bonuses.length === 0) return

        const { currency, currencyHideCents, buyBonusConfig: config } = useConfigurationStore.getState()
        const { findElementByImageName, cachedImages } = useBuyBonusStore.getState()

        const descriptionPrefix = `${translate('common.promo.congratulations')} ${translate('gameui.bonusSystem.youHaveBeenAwarded')} `

        if (bonuses.length >= 2) {
            showReactUINotification({
                type: NotificationType.BONUSES_ACTION_MULTIPLE,
                description: (
                    <span>
                        {descriptionPrefix}
                        <Translate
                            components={{
                                b: <b />,
                            }}
                            tKey="gameui.bonusSystem.notifications.multipleBonusesSuffix"
                            placeholders={{ bonusesAmount: bonuses.length }}
                        />
                    </span>
                ),
                bonuses,
            })

            return
        }

        const singleBonus = bonuses[0]
        const isBonusForCurrentGame = +singleBonus.gin === gin

        const baseBetLabel = isBonusForCurrentGame
            ? ` x ${formatCurrency(+singleBonus.betValue, currency, {
                  hideCents: currencyHideCents,
              })}`
            : ''

        switch (singleBonus.type) {
            case BonusTypes.FREE_SPIN: {
                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_FS,
                    description: (
                        <span>
                            {descriptionPrefix}
                            <Translate
                                components={{
                                    b: <b />,
                                }}
                                tKey="gameui.bonusSystem.notifications.freeSpinsSuffix"
                                placeholders={{
                                    remainingSpins: singleBonus.remainingAmount ?? 0,
                                    bet: baseBetLabel,
                                }}
                                count={+(singleBonus.remainingAmount ?? 0)}
                            />
                        </span>
                    ),
                    bonuses: [singleBonus],
                })
                break
            }
            case BonusTypes.FREE_BUY_BONUS: {
                const freeBuyBonus = modifyFreeBuyBonuses([singleBonus], config.features)

                setFBBonuses(freeBuyBonus)

                const bonusForNotification = freeBuyBonus[0]

                if (!bonusForNotification) return

                const typeConfig = bonusForNotification.typeConfiguration
                const fbbConfig = typeConfig?.freeBuyBonusConfiguration

                let resolvedIconStyle: CSSProperties | undefined

                if (fbbConfig) {
                    const matchesActiveBonus = (feature: BuyBonusFeature) =>
                        feature.name === fbbConfig.featureId || feature.guaranteedName === fbbConfig.guaranteedName

                    const selectedFeatureIndex = config.features.findIndex(matchesActiveBonus)

                    const featureImageName = `${FEATURE_IMAGE_PREFIX}${selectedFeatureIndex + 1}.png`

                    let imageData = findElementByImageName(featureImageName)

                    if (!imageData) {
                        imageData = findElementByImageName(FALLBACK_IMAGE_NAME)
                    }

                    if (imageData && cachedImages[imageData.atlasName]) {
                        resolvedIconStyle = buildAtlasImageStyles(
                            imageData.atlasName,
                            imageData.value,
                            cachedImages[imageData.atlasName]
                        )
                    }
                }

                const bonusName = fbbConfig?.label || translate('gameui.buyBonus')

                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_FBB,
                    description: (
                        <span>
                            {descriptionPrefix}
                            <Translate
                                components={{
                                    b: <b />,
                                }}
                                tKey="gameui.bonusSystem.notifications.freeBuyBonusSuffix"
                                placeholders={{ bonusName, bet: baseBetLabel }}
                            />
                        </span>
                    ),
                    bonuses: [bonusForNotification],
                    iconStyle: resolvedIconStyle,
                })
                break
            }
            case BonusTypes.COUNTDOWN_FREE_SPIN: {
                const countdown = getCountdownValuesFromBonus(singleBonus, true)

                if (!countdown) return null

                const displayItems: CountdownCounterDisplayItems = [
                    { val: countdown.days, label: translate('gameui.bonusSystem.timeLeftDays') },
                    { val: countdown.hours, label: translate('gameui.bonusSystem.timeLeftHours') },
                    { val: countdown.minutes, label: translate('gameui.bonusSystem.timeLeftMinutes') },
                    { val: countdown.seconds, label: translate('gameui.bonusSystem.timeLeftSeconds') },
                ]

                const { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel } =
                    getCountdownDisplayItems(displayItems)
                const countdownFreespinTimeLabel = `${firstStringDigits}${firstStringLabel}${secondStringDigits}${secondStringLabel}`

                showReactUINotification({
                    type: NotificationType.BONUSES_ACTION_CFS,
                    description: (
                        <span>
                            {descriptionPrefix}
                            <Translate
                                components={{
                                    b: <b />,
                                }}
                                tKey="gameui.bonusSystem.notifications.countdownFreeSpinsSuffix"
                                placeholders={{ bet: baseBetLabel, timeLabel: countdownFreespinTimeLabel }}
                            />
                        </span>
                    ),
                    bonuses: [singleBonus],
                })
                break
            }
            default:
                break
        }
    }

    const handlePlayNow = (bonus?: Bonus) => {
        if (!bonus) return

        const message: BonusesPlayNowMessage = {
            type: GameUIMessageType.BONUSES_PLAY_NOW,
            payload: { bonusIds: [bonus.id] },
        }

        window.postMessage(message, '*')

        switch (bonus.type) {
            case BonusTypes.FREE_BUY_BONUS: {
                setHasFBBActivatedBonus(true)
                setFBBActiveBonus(bonus)
                setFBBonusSystemState(BonusSystemState.IN_PLAY)

                if (hasBuyBonus && buyBonusConfig?.shouldTheFeatureStayActive) {
                    const freeBuyBonusConfig = bonus.typeConfiguration?.freeBuyBonusConfiguration

                    const matchesActiveBonus = (feature: BuyBonusFeature) =>
                        feature.name === freeBuyBonusConfig?.featureId ||
                        feature.guaranteedName === freeBuyBonusConfig?.guaranteedName

                    const selectedFeatureIndex = buyBonusConfig.features.findIndex(matchesActiveBonus)
                    const currentFeature =
                        selectedFeatureIndex !== -1 ? buyBonusConfig.features[selectedFeatureIndex] : null

                    setSelectedFeatureIndex(selectedFeatureIndex)
                    setSelectedFeature(currentFeature)
                    updateCurrentFeatureMultiplier(currentFeature?.multiplier ?? 1)
                    setMultiChoiceToggle(true)
                }

                break
            }
            case BonusTypes.FREE_SPIN: {
                setFSActiveBonus(bonus)
                setFSBonusSystemState(BonusSystemState.IN_PLAY)
                break
            }
            case BonusTypes.COUNTDOWN_FREE_SPIN: {
                setCFSActiveBonus(bonus)
                setCFSBonusSystemState(BonusSystemState.IN_PLAY)
                break
            }
            default:
                break
        }
    }

    return { handlePlayNow, showBonusAwardedNotification }
}

export default useBonusSystem
